import { WhiteboardPageInfo } from '@/micro-apps/Whiteboard/interfaces';
import { ROLE } from '@/modules/lightspeed/webinar/core/controllers/user-extras-client';
import { NotificationDocument } from '@/modules/session/model/store/PointsStore';
import { SessionProps } from '@/modules/session/views/types';

import { ResourceCategoryEnum } from '../../../types/schema';
import { Reactions } from '../consts';
import Model from '../model';
import { ConfettiDocument } from './Confetti';
import { ExternalSourceStates } from './ExternalCamera';
import {
  WhiteboardMetadata,
  WhiteboardOnMaterialStageInfo,
  WhiteboardOnVideoMetaData,
  WhiteboardOnVideoStageInfo,
  WhiteboardStageInfo,
} from './WhiteBoard';

type Stream = any;

export enum Tabs {
  Participants = 'PARTICIPANTS',
  Chat = 'CHAT',
  Activity = 'ACTIVITY',
  ClassInfo = 'CLASS_INFO',
}

export enum Permission {
  Allowed = 'ALLOWED',
  Not_Allowed = 'NOT_ALLOWED',
}

export enum ClassroomUserStatus {
  InClass = 'IN_CLASS',
  Left = 'LEFT',
}

export enum ConferenceLayoutTypes {
  Grid = 'GRID',
  Speaker = 'SPEAKER',
}

export enum GridLayoutTypes {
  Small = 'SMALL',
  Large = 'LARGE',
}

export enum SpeakerLayoutTypes {
  Floating = 'FLOATING',
  SideBySide = 'SIDE_BY_SIDE',
}

export enum ActiveStatus {
  Active = 'a',
  InActive = 'i',
  Left = 'l',
}

export enum DocumentTypes {
  User = 'USER',
}

export interface Publication {
  audio: boolean;
  video: boolean;
}

export interface ObserverComponentProps {
  model: Model;
}

export interface StreamWithUserDetails extends Stream {
  isLocalStream?: boolean;
  isScreenShare?: boolean;
}

export interface SessionParticipantDocument {
  documentType: DocumentTypes.User;
  userId: string;
  companyId: string;
  name: string;
  picture: string;
  isRaisingHand: boolean;
  allowMicroapps?: boolean;
  jitsiId?: string;
  jitsiScreenId?: string;
  activeStatus: ActiveStatus;
  reaction?: ReactionDocument;
  notification?: NotificationDocument;
  confetti?: ConfettiDocument;
  priority?: number;
  externalSourceState?: ExternalSourceStates;
}

export interface LiveSessionUser {
  id: string;
  name: string;
  emailId: string;
  profilePicture: string;
  role?: ROLE;
}

export interface EventUsers {
  node: LiveSessionUser;
}

export interface EventUsersResponse {
  eventUsers: {
    edges: EventUsers[];
  };
}

export interface EventDataResponse {
  event: Event;
}

export interface LiveSessionUsers {
  userId: string;
  name: string;
  emailId: string;
  picture?: string;
  role?: ROLE;
}

export type FirebaseDocument = SessionParticipantDocument;

export enum StageTypes {
  VideoGrid = 'VIDEO_GRID',
  Whiteboard = 'WHITEBOARD',
  WhiteboardOnVideo = 'WHITEBOARD_ON_VIDEO',
  Material = 'MATERIAL',
  TextEditor = 'TEXT_EDITOR',
  VideoShare = 'VIDEO_SHARE',
  Excalidraw = 'EXCALIDRAW',
  Course = 'COURSE',
}

export enum VideoPlayerState {
  Pause = 'PAUSE',
  Play = 'PLAY',
}

export interface TextEditorStageInfo {
  type: StageTypes.TextEditor;
  createdBy: string;
}

export interface ExcalidrawStageInfo {
  type: StageTypes.Excalidraw;
  createdBy: string;
}

export interface VideoShareStageInfo {
  type: StageTypes.VideoShare;
}

export interface VideoGridStageInfo {
  showWhiteboard?: boolean;
  type: StageTypes.VideoGrid;
}

export type Stage =
  | WhiteboardOnMaterialStageInfo
  | WhiteboardStageInfo
  | VideoGridStageInfo
  | TextEditorStageInfo
  | WhiteboardOnVideoStageInfo
  | VideoShareStageInfo
  | ExcalidrawStageInfo;

export interface RecordingInfo {
  recordingId: string;
  createdAt: number;
}

export interface ClassroomDocument {
  ap?: number;
  wp?: number;
  name?: string;
  stage: Stage;
  recording?: RecordingInfo;
  meta?: Metadata;
}

export interface Metadata {
  [StageTypes.Whiteboard]?: WhiteboardMetadata;
  [StageTypes.Material]?: StudyMaterialMetadata;
  [StageTypes.WhiteboardOnVideo]?: WhiteboardOnVideoMetaData;
  [StageTypes.Course]?: CourseMetaData;
  [StageTypes.VideoGrid]?: StageMetaData; //Will be added in the future
  [StageTypes.TextEditor]?: StageMetaData; //Will be added in the future
  [StageTypes.VideoShare]?: VideoShareMetaData; //Will be added in the future
  [StageTypes.Excalidraw]?: StageMetaData; //Will be added in the future
}

export interface StageMetaData {}

export interface StudyMaterialMetadata extends StageMetaData {
  pageInfo: WhiteboardPageInfo;
  currentPageUrl: string;
  materialId: string; // TODO: remove materialId
  materialName: string; // TODO: remove materialName
  material?: Material;
  showWhiteboard: boolean;
}

export interface CourseMetaData extends StageMetaData {
  createdBy: string;
  courseId: string | null;
  lessonPageId: string | null;
  scrollPosition: number;
  isFollowingInstructor: boolean;
  setFollowState: (newState: Record<string, object>) => {};
  followState: Record<string, object>;
  workspaceCourseIdMap: Record<string, string>;
}

export interface VideoShareMetaData extends StageMetaData {
  createdBy: string;
  url: string;
  playerState: VideoPlayerState;
  videoElapsed: number;
  lastStateUpdatedAt: number;
}

export type Material = {
  id: string;
  name: string;
  category: ResourceCategoryEnum;
  extension: string | undefined;
  size: number;
  imageFileUrls: string[];
};

declare global {
  interface FileList {
    forEach(callback: (f: File) => void): void;
  }
}

export enum JobIdFirebaseStatus {
  Processed = 'processed',
  Completed = 'completed',
}

export enum ActiveScreen {
  Loading = 'LOADING',
  StartScreen = 'START_SCREEN',
  WaitingScreen = 'WAITING_SCREEN',
  Classroom = 'CLASSROOM',
  EndScreen = 'END_SCREEN',
}

export type ConferenceInfo = SessionProps['eventInfo'];

export enum EventType {
  Lecture = 'LECTURE',
  Exam = 'EXAM',
}

export interface SessionContextType {
  model: Model;
}

export enum ConferenceStatus {
  UNKNOWN = 'UNKNOWN',
  CONNECTING = 'CONNECTING',
  LIVE = 'LIVE',
  ENDED = 'ENDED',
  ERROR = 'ERROR',
}

export enum NetworkStatus {
  OFFLINE = 'OFFLINE',
  ONLINE = 'ONLINE',
}

export enum StreamLayoutOverflowMode {
  scroll = 'SCROLL',
  pagination = 'PAGINATION',
}

export type Track = any;

export type Stats = any;

export type Connection = any;

export type Conference = any;

export type Devices = 'audio' | 'video' | 'desktop';

export type TrackType = 'audio' | 'video';

export type ConferenceTrackHolder = {
  video?: Track;
  audio?: Track;
  isConnectionActive?: boolean;
};

export type ReactionTypes = keyof typeof Reactions;

export interface ReactionDocument {
  createdAt: number;
  type: ReactionTypes;
}

export enum ConferenceEvents {
  CONFERENCE_JOINED = 'conference.CONFERENCE_JOINED',
  USER_JOINED = 'conference.USER_JOINED',
  USER_LEFT = 'conference.USER_LEFT',
  DOMINANT_SPEAKER_CHANGED = 'conference.DOMINANT_SPEAKER_CHANGED',
  PARTICIPANT_CONN_STATUS_CHANGED = 'conference.PARTICIPANT_CONN_STATUS_CHANGED',
  TRACK_ADDED = 'conference.TRACK_ADDED',
  TRACK_REMOVED = 'conference.TRACK_REMOVED',
  TRACK_MUTE_CHANGED = 'conference.TRACK_MUTE_CHANGED',
  LOCAL_STATS_UPDATED = 'connectionQuality.LOCAL_STATS_UPDATED',
  REMOTE_STATS_UPDATED = 'connectionQuality.REMOTE_STATS_UPDATED',
}

export enum ParticipantConnectionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  INTERRUPTED = 'interrupted',
}

export enum ConnectionQualityStatus {
  Good = 'Session:Good',
  Medium = 'Session:Medium',
  Poor = 'Session:Poor',
}

export enum ConnectionEvents {
  CONNECTION_ESTABLISHED = 'connection.CONNECTION_ESTABLISHED',
  CONNECTION_DISCONNECTED = 'connection.CONNECTION_DISCONNECTED',
  CONNECTION_FAILED = 'connection.CONNECTION_FAILED',
  WRONG_STATE = 'connection.WRONG_STATE',
}

export interface MicroAppPermissions {
  canEdit: boolean;
  canManage: boolean;
}

export * from './Confetti';
export * from './ExternalCamera';
